import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, getDocs, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { API_BASE_URL } from '../config';

interface Lecture {
  id: string;
  name: string;
  summary: string;
  transcript: string;
  createdAt: Date;
  duration?: string; // Optional duration field
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [lectures, setLectures] = useState<Lecture[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [selectedLecture, setSelectedLecture] = useState<Lecture | null>(null);
  const [recordingStartTime, setRecordingStartTime] = useState<Date | null>(null);

  const fetchLectures = useCallback(async () => {
    if (!user) return;
    
    try {
      const lecturesRef = collection(db, 'lectures');
      const q = query(lecturesRef, where('userId', '==', user.uid));
      const querySnapshot = await getDocs(q);
      
      const lectureData: Lecture[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        let duration = "00:00";
        if (data.duration) {
          duration = data.duration;
        }
        
        lectureData.push({ 
          id: doc.id, 
          ...data,
          duration,
          createdAt: data.createdAt.toDate() 
        } as Lecture);
      });
      
      // Sort lectures by creation date (newest first)
      lectureData.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      
      setLectures(lectureData);
    } catch (error) {
      console.error('Error fetching lectures:', error);
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
    } else {
      fetchLectures();
    }
  }, [user, navigate, fetchLectures]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks: BlobPart[] = [];

      recorder.ondataavailable = (e) => {
        chunks.push(e.data);
      };

      recorder.onstop = async () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        await handleAudioUpload(blob);
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
      setRecordingStartTime(new Date());
    } catch (error) {
      console.error('Error starting recording:', error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
      setIsRecording(false);
      setRecordingStartTime(null);
    }
  };

  const formatDuration = (startTime: Date | null): string => {
    if (!startTime) return "00:00";
    
    const now = new Date();
    const diffMs = now.getTime() - startTime.getTime();
    const minutes = Math.floor(diffMs / 60000);
    const seconds = Math.floor((diffMs % 60000) / 1000);
    
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatDate = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  const handleAudioUpload = async (blob: Blob) => {
    if (!user) return;

    try {
      // Create a FormData object to send the audio file
      const formData = new FormData();
      formData.append('file', blob, 'recording.webm');

      // Upload and transcribe the audio
      const transcribeResponse = await fetch(`${API_BASE_URL}/api/transcribe`, {
        method: 'POST',
        body: formData,
      });
      
      if (!transcribeResponse.ok) {
        throw new Error('Failed to transcribe audio');
      }
      
      const { transcript } = await transcribeResponse.json();

      // Generate summary
      const summaryResponse = await fetch(`${API_BASE_URL}/api/summarize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ transcript }),
      });

      if (!summaryResponse.ok) {
        throw new Error('Failed to generate summary');
      }

      const { summary, title } = await summaryResponse.json();

      // Create a new lecture document in Firestore
      const lectureData = {
        name: title,
        summary,
        transcript,
        createdAt: new Date(),
        userId: user.uid,
        duration: formatDuration(recordingStartTime),
      };

      await addDoc(collection(db, 'lectures'), lectureData);
      
      // Refresh the lectures list
      await fetchLectures();
    } catch (error) {
      console.error('Error processing audio:', error);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-primary">Notegenius</h1>
          <button
            onClick={handleSignOut}
            className="text-gray-600 hover:text-gray-800"
          >
            Sign Out
          </button>
        </div>
      </nav>

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Record New Lecture Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Record New Lecture</h2>
          <div className="bg-white rounded-lg shadow p-6">
            <button
              onClick={isRecording ? stopRecording : startRecording}
              className={`w-full flex items-center justify-center gap-2 px-6 py-4 rounded-lg font-semibold text-white ${
                isRecording ? 'bg-red-500 hover:bg-red-600' : 'bg-primary hover:bg-indigo-700'
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
              </svg>
              {isRecording ? 'Stop Recording' : 'Start Recording'}
            </button>
          </div>
        </div>

        {/* Previous Lectures Section */}
        <div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Previous Lectures</h2>
          <div className="space-y-4">
            {lectures.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No lectures recorded yet</p>
            ) : (
              lectures.map((lecture) => (
                <div
                  key={lecture.id}
                  className="bg-white p-6 rounded-lg shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start flex-1 min-w-0 pr-4">
                      <div className="flex-shrink-0 mt-1">
                        <img src="/document-icon.svg" alt="Document" className="w-6 h-6 text-primary" />
                      </div>
                      <div className="ml-4 overflow-hidden">
                        <h3 className="text-xl font-semibold text-gray-800 break-words">{lecture.name}</h3>
                        <div className="flex items-center text-sm text-gray-500 mt-1">
                          <span>{formatDate(lecture.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-4 flex-shrink-0 ml-4">
                      <button
                        onClick={() => {
                          setSelectedLecture(lecture);
                          // Set view to summary
                        }}
                        className="text-primary hover:text-indigo-700 font-medium whitespace-nowrap"
                      >
                        View Notes
                      </button>
                      <button
                        onClick={() => {
                          setSelectedLecture(lecture);
                          // Set view to transcript
                        }}
                        className="text-primary hover:text-indigo-700 font-medium whitespace-nowrap"
                      >
                        Transcript
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </main>

      {/* Lecture Details Modal */}
      {selectedLecture && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">{selectedLecture.name}</h2>
              <button
                onClick={() => setSelectedLecture(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                Close
              </button>
            </div>
            <div className="prose max-w-none">
              <h3 className="text-xl font-semibold mb-2">Summary</h3>
              <p className="mb-4">{selectedLecture.summary}</p>
              <h3 className="text-xl font-semibold mb-2">Transcript</h3>
              <p>{selectedLecture.transcript}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard; 